import React, { createContext, useContext, useState, useCallback } from "react";

const translations = {
  en: {
    nav: {
      freelancers: "Freelancers",
      about: "About",
      project: "Describe your project",
    },
    hero: {
      eyebrow: "Based in Ericeira, Portugal",
      title_1: "Find freelancers in",
      title_2: "Ericeira",
      title_3: "for your next project",
      subtitle:
        "Freelancers based in Ericeira. See their work, reach out directly, or tell us what you need.",
      placeholder: "What are you looking for?",
      view: "View freelancers",
      describe: "Describe your project",
    },
    trust: "Independent professionals based in Ericeira",
    how: {
      label: "How it works",
      title: "A simple, human process",
      subtitle:
        "No accounts, no bidding, no noise. Just three steps to finding the right person.",
      steps: [
        {
          title: "Explore profiles",
          text: "Browse a small, curated list of freelancers based in Ericeira.",
        },
        {
          title: "Choose or describe",
          text: "Pick someone directly, or tell us about your project in a short form.",
        },
        {
          title: "Connect directly",
          text: "Reach out to the freelancer or get a personal recommendation from us.",
        },
      ],
    },
    freelancers: {
      label: "The network",
      title: "Meet the freelancers",
      subtitle:
        "A group of independent professionals, available for new projects.",
      view_profile: "View profile",
      view_all: "View all freelancers",
    },
    why: {
      label: "Why this site",
      title: "A simpler way to find the right person",
      items: [
        {
          title: "Real freelancers, already working",
          text: "Everyone listed lives and works in Ericeira. No anonymous profiles.",
        },
        {
          title: "Direct contact, no platform",
          text: "See what they actually do and reach out without a middleman.",
        },
        {
          title: "Less noise, clearer choices",
          text: "No bidding, no spam, no hundreds of lookalike profiles.",
        },
        {
          title: "A simple way to find trust",
          text: "Every profile here belongs to someone who is already freelancing and has real work to show.",
        },
      ],
    },
    form: {
      label: "Project inquiry",
      title: "Not sure who to contact",
      subtitle:
        "Describe your project and we'll suggest someone from the network.",
      name: "Your name",
      email: "Email",
      project_type: "Type of project",
      project_type_placeholder: "e.g. Website, branding, automation…",
      budget: "Budget range",
      budget_placeholder: "Select a range",
      budget_options: [
        "Under €500",
        "€500 – €1,500",
        "€1,500 – €5,000",
        "€5,000 – €15,000",
        "€15,000+",
        "Not sure yet",
      ],
      message: "Message",
      message_placeholder: "Tell us a bit about what you need.",
      submit: "Send project",
      sending: "Sending…",
      success: "Thanks — we'll get back to you shortly.",
      error: "Something went wrong. Please try again.",
    },
    about: {
      label: "About",
      title: "A network of working freelancers in Ericeira",
      text:
        "This is a group of freelancers based in Ericeira who are already working with clients and have real projects behind them. Explore their work, reach out directly, and find the right person for what you need.",
    },
    cta: {
      title: "Have a project in mind?",
      subtitle:
        "Pick someone directly, or tell us what you need and we'll help.",
      view: "View freelancers",
      describe: "Describe your project",
    },
    profile: {
      back: "Back to freelancers",
      contact: "Get in touch",
      website: "Website",
      email: "Email",
      phone: "Phone",
      what_i_do: "What I do",
      selected_work: "Selected work",
      not_available: "Not available yet",
    },
    footer: {
      tagline: "A network of working freelancers based in Ericeira",
      rights: "© 2026 Ericeira Works",
     
    },
    freelancersPage: {
      title: "Freelancers in Ericeira",
      subtitle:
        "A group of independent professionals, available for new projects.",
    },
  },
  pt: {
    nav: {
      freelancers: "Freelancers",
      about: "Sobre",
      project: "Descreve o teu projeto",
    },
    hero: {
      eyebrow: "Baseado na Ericeira, Portugal",
      title_1: "Encontra freelancers na",
      title_2: "Ericeira",
      title_3: "para o teu próximo projeto",
      subtitle:
        "Uma pequena rede de profissionais independentes baseados na Ericeira. Explora o trabalho real, contacta diretamente, ou descreve o teu projeto e ajudamos a encontrar a pessoa certa.",
      placeholder: "O que procuras?",
      view: "Ver freelancers",
      describe: "Descreve o teu projeto",
    },
    trust: "Profissionais independentes baseados na Ericeira",
    how: {
      label: "Como funciona",
      title: "Um processo simples e humano",
      subtitle:
        "Sem contas, sem leilões, sem ruído. Apenas três passos para encontrares a pessoa certa.",
      steps: [
        {
          title: "Explora os perfis",
          text: "Vê uma lista pequena e curada de freelancers baseados na Ericeira.",
        },
        {
          title: "Escolhe ou descreve",
          text: "Escolhe alguém diretamente, ou conta-nos o teu projeto num formulário curto.",
        },
        {
          title: "Contacta diretamente",
          text: "Fala com o freelancer ou recebe uma recomendação pessoal nossa.",
        },
      ],
    },
    freelancers: {
      label: "A rede",
      title: "Conhece os freelancers",
      subtitle:
        "Um pequeno grupo de profissionais independentes, todos baseados na Ericeira.",
      view_profile: "Ver perfil",
      view_all: "Ver todos os freelancers",
    },
    why: {
      label: "Porquê este site",
      title: "Uma forma mais calma de encontrar alguém em quem confiar",
      items: [
        {
          title: "Pessoas reais, daqui",
          text: "Todos os listados vivem e trabalham na Ericeira. Sem perfis anónimos.",
        },
        {
          title: "Trabalho visível, contacto direto",
          text: "Vê o que cada um faz e contacta sem intermediários.",
        },
        {
          title: "Menos ruído que grandes plataformas",
          text: "Sem leilões, sem spam, sem centenas de perfis iguais.",
        },
        {
          title: "Uma forma simples de confiar",
          text: "Uma rede pequena e curada facilita a escolha com confiança.",
        },
      ],
    },
    form: {
      label: "Pedido de projeto",
      title: "Não sabes quem escolher?",
      subtitle:
        "Descreve o teu projeto e sugerimos alguém da rede.",
      name: "O teu nome",
      email: "Email",
      project_type: "Tipo de projeto",
      project_type_placeholder: "ex. Website, branding, automação…",
      budget: "Orçamento",
      budget_placeholder: "Seleciona um intervalo",
      budget_options: [
        "Menos de €500",
        "€500 – €1.500",
        "€1.500 – €5.000",
        "€5.000 – €15.000",
        "€15.000+",
        "Ainda não sei",
      ],
      message: "Mensagem",
      message_placeholder: "Conta-nos um pouco sobre o que precisas.",
      submit: "Enviar projeto",
      sending: "A enviar…",
      success: "Obrigado — respondemos em breve.",
      error: "Algo correu mal. Tenta novamente.",
    },
    about: {
      label: "Sobre",
      title: "Um projeto local e simples",
      text:
        "Este site foi criado para tornar mais fácil encontrar freelancers independentes baseados na Ericeira. Não é um marketplace nem uma plataforma com centenas de perfis — apenas um pequeno grupo de pessoas a fazer trabalho real.",
    },
    cta: {
      title: "Tens um projeto em mente?",
      subtitle:
        "Escolhe alguém diretamente, ou diz-nos o que precisas e ajudamos.",
      view: "Ver freelancers",
      describe: "Descreve o teu projeto",
    },
    profile: {
      back: "Voltar aos freelancers",
      contact: "Entra em contacto",
      website: "Website",
      email: "Email",
      phone: "Telefone",
      what_i_do: "O que faço",
      selected_work: "Trabalho selecionado",
      not_available: "Ainda não disponível",
    },
    footer: {
      tagline: "Uma pequena rede local de freelancers baseados na Ericeira.",
      rights: "© 2025 Ericeira Works. Um projeto local simples.",
    },
    freelancersPage: {
      title: "Freelancers na Ericeira",
      subtitle:
        "Uma rede pequena e curada de profissionais independentes baseados na Ericeira.",
    },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window !== "undefined") {
      return window.localStorage.getItem("ef_lang") || "en";
    }
    return "en";
  });

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === "en" ? "pt" : "en";
      if (typeof window !== "undefined") {
        window.localStorage.setItem("ef_lang", next);
      }
      return next;
    });
  }, []);

  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
